import { Link } from "react-router-dom";
import { Navbar } from "../../components/Navbar";
import { Footer } from "../../components/Footer";

/**
 * Terms of Service.
 *
 * Known gaps — fill as they're resolved:
 * - Legal entity name + registered address (Companies House cert pending; "Intakall AI" trading name until then).
 * - Paid plans referenced as available "in future" — fee/refund language tightens once Stripe goes live.
 * - Notice of material changes is currently "we'll email registered users" — once an in-product banner ships, add it here.
 */
export default function Terms() {
  return (
    <div className="min-h-screen bg-white font-body text-slate-800">
      <Navbar />
      <main>

      <article className="mx-auto max-w-3xl px-6 py-12 leading-relaxed">
        <header className="mb-10 border-b border-slate-200 pb-6">
          <h1 className="text-4xl font-semibold text-slate-900">Terms of Service</h1>
          <p className="mt-2 text-sm text-slate-500">
            Effective date: 2026-05-23
            <span className="mx-2">·</span>
            Last updated: 2026-09-19
          </p>
          <div className="mt-6 rounded border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
            <p>
              These terms form the contract between you and Intakall when you use
              the Intakall Chrome extension, Google Meet add-on, dashboard, or
              related services (together, the <strong>"Service"</strong>). They
              sit alongside our{" "}
              <Link to="/privacy" className="text-indigo-600 hover:underline">
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link to="/security" className="text-indigo-600 hover:underline">
                Security Trust Centre
              </Link>
              .
            </p>
          </div>
        </header>

        <Section n="1" title="Acceptance & changes">
          <p>
            By creating an Intakall account, installing the extension, or using
            the Service, you agree to these Terms. If you are using Intakall on
            behalf of an organisation, you confirm you have authority to bind
            that organisation, and "you" refers to both you and the organisation.
          </p>
          <p>
            We may update these Terms from time to time. If a change is material,
            we will give registered users reasonable advance notice by email to
            the address on file. Continued use of the Service after the change
            takes effect means you accept the updated Terms. If you don't, your
            remedy is to stop using the Service and close your account.
          </p>
        </Section>

        <Section n="2" title="What Intakall provides">
          <p>
            Intakall surfaces live context from sources you connect — such as
            GitHub, Linear, and Slack — directly into your meetings on Google
            Meet (and, over time, Zoom and Microsoft Teams). Recording, when
            enabled, produces a transcript and AI-generated cards (suggestions,
            ticket drafts, action items) that you can review, dismiss, or act on.
          </p>
          <p>
            The Service is offered as software-as-a-service. We host and operate
            it; you access it through the surfaces above. We may add, change, or
            remove features as the product evolves — we'll avoid breaking
            material functionality without notice where we reasonably can.
          </p>
          <p>
            Intakall is in active development. Some features are explicitly
            labelled as <strong>beta</strong>, <strong>preview</strong>, or
            <strong> early access</strong>. Those features may change, break, or
            be withdrawn without notice and are not covered by any service-level
            promises.
          </p>
        </Section>

        <Section n="3" title="Your account & eligibility">
          <p>
            You need a Google account to sign in. You must be at least 18 years
            old, or the age of majority where you live if that is higher, to use
            the Service. The Service is not intended for use by children.
          </p>
          <p>
            You are responsible for activity that happens under your account,
            for keeping your credentials secure, and for ensuring that anyone
            you invite to your team is authorised to access the meeting content
            you'll share with them. If you suspect unauthorised access, contact
            us at{" "}
            <a href="mailto:security@intakall.io" className="text-indigo-600 hover:underline">
              security@intakall.io
            </a>{" "}
            immediately.
          </p>
        </Section>

        <Section n="4" title="Acceptable use">
          <p>
            You agree not to use the Service to:
          </p>
          <ul className="ml-6 list-disc space-y-2">
            <li>
              <strong>Record without consent.</strong> You must comply with the
              recording, notice, and consent laws of every jurisdiction where
              meeting participants are located. You are responsible for telling
              participants that the meeting is being recorded and transcribed,
              and for getting any consent that local law or your own
              organisation's policies require.
            </li>
            <li>
              Use the Service to capture content from meetings you are not
              entitled to attend or record.
            </li>
            <li>
              Reverse engineer, decompile, scrape, or attempt to derive the
              source code or model weights of the Service, except to the extent
              permitted by law that cannot be waived by contract.
            </li>
            <li>
              Probe or attack the Service's security or availability, except
              under a written authorised-testing agreement with us.
            </li>
            <li>
              Use the Service to process content that you do not have the rights
              to process, that is unlawful, that infringes third-party rights,
              or that is otherwise prohibited by applicable law.
            </li>
            <li>
              Resell, sublicense, or provide the Service as a managed service to
              third parties without a separate written agreement with us.
            </li>
            <li>
              Use the Service in a way that disrupts other users, evades
              rate-limits, or imposes an unreasonable load on our infrastructure.
            </li>
          </ul>
          <p>
            We may suspend or terminate accounts that violate this section, with
            or without notice depending on severity.
          </p>
        </Section>

        <Section n="5" title="Fees, plans & free tier">
          <p>
            During the v0.1 launch period the Service is offered free of charge.
            We may introduce paid plans in future; if we do, we will give
            registered users reasonable notice before any plan you depend on
            transitions from free to paid, and you will have the option to
            decline and stop using the affected plan.
          </p>
          <p>
            When paid plans become available, fees will be billed in advance on
            the cadence shown at checkout (monthly or annual). Taxes, where
            applicable, are added at checkout. Except where required by law,
            fees are non-refundable; we may offer pro-rata refunds at our
            discretion in clear cases of our error.
          </p>
        </Section>

        <Section n="6" title="Your content & data">
          <p>
            You retain ownership of the content you provide to or process
            through the Service — including meeting audio, transcripts, drafts,
            and the data we pull from integrations you connect. You grant
            Intakall a worldwide, non-exclusive, royalty-free licence to host,
            transmit, and process that content solely to provide the Service to
            you and your team.
          </p>
          <p>
            We do not train AI models on your content, and our subprocessors are
            configured to exclude your content from their model training. See
            the <Link to="/privacy" className="text-indigo-600 hover:underline">Privacy Policy</Link>{" "}
            for the details, retention periods, and the{" "}
            <Link to="/subprocessors" className="text-indigo-600 hover:underline">Subprocessors</Link>{" "}
            page for the current vendor list.
          </p>
          <p>
            For B2B customers requiring a Data Processing Agreement, email{" "}
            <a href="mailto:privacy@intakall.io" className="text-indigo-600 hover:underline">
              privacy@intakall.io
            </a>
            .
          </p>
        </Section>

        <Section n="7" title="Intellectual property">
          <p>
            The Service, including its software, models, UI, brand, and
            documentation, is and remains the property of Intakall and its
            licensors. We grant you a limited, revocable, non-exclusive,
            non-transferable licence to use the Service for its intended purpose
            during the term of your subscription or use.
          </p>
          <p>
            We welcome feedback. If you send us suggestions, ideas, or other
            feedback, you grant us a perpetual, irrevocable, royalty-free
            licence to use that feedback to improve the Service, with no
            obligation to compensate or credit you.
          </p>
        </Section>

        <Section n="8" title="Third-party integrations">
          <p>
            When you connect a third-party service such as GitHub, Linear,
            Slack, or Google Workspace, you authorise Intakall to access that
            service on your behalf using the scopes you grant. Your use of those
            third-party services is governed by their own terms; we are not
            responsible for outages, policy changes, or actions taken by those
            third parties, and we may have to disable an integration if a
            provider changes their API or terms in a way that prevents us from
            offering it safely.
          </p>
        </Section>

        <Section n="9" title="Termination">
          <p>
            You may stop using the Service and close your account at any time
            from the dashboard or by emailing{" "}
            <a href="mailto:hello@intakall.io" className="text-indigo-600 hover:underline">
              hello@intakall.io
            </a>
            . On closure we will delete or de-identify your content in line with
            the retention schedule in the{" "}
            <Link to="/privacy" className="text-indigo-600 hover:underline">Privacy Policy</Link>
            .
          </p>
          <p>
            We may suspend or terminate your access if you materially breach
            these Terms, if continued provision exposes us or other users to
            legal risk, or if we discontinue the Service. Where we discontinue
            the Service, we will give registered users reasonable advance notice
            and a way to export their data.
          </p>
        </Section>

        <Section n="10" title="Disclaimers & limitation of liability">
          <p>
            The Service is provided <strong>"as is"</strong> and{" "}
            <strong>"as available"</strong>. To the maximum extent permitted by
            law, we disclaim all warranties — express, implied, or statutory —
            including warranties of merchantability, fitness for a particular
            purpose, and non-infringement. Intakall produces AI-generated
            output, including transcripts and suggestions; that output can be
            wrong, incomplete, or out of date, and you are responsible for
            reviewing it before acting on it.
          </p>
          <p>
            To the maximum extent permitted by law, neither party will be liable
            for any indirect, incidental, special, consequential, or punitive
            damages, or for lost profits, revenue, or data. Our total aggregate
            liability arising out of or relating to the Service or these Terms
            will not exceed the greater of (a) the fees you paid Intakall in the
            12 months preceding the event giving rise to the claim, or (b) one
            hundred British pounds (£100).
          </p>
          <p>
            Nothing in these Terms limits or excludes any liability that cannot
            lawfully be limited or excluded — including liability for death or
            personal injury caused by negligence, fraud, or fraudulent
            misrepresentation.
          </p>
        </Section>

        <Section n="11" title="Indemnity">
          <p>
            You will defend, indemnify, and hold Intakall harmless from
            third-party claims arising from your breach of these Terms, your
            violation of applicable law, or your use of the Service in a way
            that infringes the rights of a third party — including claims that
            you recorded a meeting without the consent required in your
            jurisdiction.
          </p>
        </Section>

        <Section n="12" title="Governing law & disputes">
          <p>
            These Terms are governed by the laws of England and Wales. The
            courts of England and Wales have exclusive jurisdiction over any
            dispute arising out of or relating to these Terms or the Service,
            except that we may seek injunctive or equitable relief in any
            jurisdiction to protect our intellectual property or confidential
            information.
          </p>
          <p>
            If you are a consumer resident in the EU, EEA, or UK, this clause
            does not deprive you of the protection of the mandatory consumer
            laws of your country of residence.
          </p>
        </Section>

        <Section n="13" title="Miscellaneous">
          <p>
            <strong>Entire agreement.</strong> These Terms, together with the
            Privacy Policy and any other documents we reference, are the entire
            agreement between you and Intakall and supersede prior agreements
            on the same subject.
          </p>
          <p>
            <strong>Severability.</strong> If any part of these Terms is held
            unenforceable, the rest remains in effect and the unenforceable
            part is reformed to the minimum extent needed to make it
            enforceable.
          </p>
          <p>
            <strong>Assignment.</strong> You may not assign these Terms without
            our written consent. We may assign these Terms to an affiliate or
            in connection with a merger, acquisition, or sale of assets, on
            notice.
          </p>
          <p>
            <strong>No waiver.</strong> Our failure to enforce a provision is
            not a waiver of our right to enforce it later.
          </p>
          <p>
            <strong>Force majeure.</strong> Neither party is liable for
            failures caused by events beyond their reasonable control,
            including network or infrastructure outages, acts of government,
            or major third-party-provider failures.
          </p>
        </Section>

        <Section n="14" title="Contact">
          <ul className="ml-6 list-disc space-y-1">
            <li>
              General:{" "}
              <a href="mailto:hello@intakall.io" className="text-indigo-600 hover:underline">
                hello@intakall.io
              </a>
            </li>
            <li>
              Privacy & data requests:{" "}
              <a href="mailto:privacy@intakall.io" className="text-indigo-600 hover:underline">
                privacy@intakall.io
              </a>
            </li>
            <li>
              Security reports:{" "}
              <a href="mailto:security@intakall.io" className="text-indigo-600 hover:underline">
                security@intakall.io
              </a>
            </li>
          </ul>
          <p className="mt-4 text-sm text-slate-500">
            Intakall AI is a pre-incorporation trading name. A registered legal
            entity will be named here on completion of UK incorporation.
          </p>
        </Section>

        <footer className="mt-16 border-t border-slate-200 pt-6 text-sm text-slate-500">
          <Link to="/" className="text-indigo-600 hover:underline">
            ← Back to Intakall
          </Link>
        </footer>
      </article>
      </main>
      <Footer />
    </div>
  );
}

function Section({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="text-2xl font-semibold text-slate-900">
        <span className="mr-3 text-slate-400">{n}.</span>
        {title}
      </h2>
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  );
}
